import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { AnimatedExplainer } from "@/components/AnimatedExplainer";
import { Chapter, Prose, TheoryBox, MTQuote, Steps, Step } from "@/components/mt/MTBox";
import { NoteImage } from "@/components/mt/NoteImage";
import { ChordTree } from "@/components/mt/ChordTree";
import { NextUp } from "@/components/Callouts";

export default function MusicTheory29() {
  const { lesson, course, accent } = findLesson("music-theory", "29");
  return (
    <LessonShell
      course={course}
      courseSlug="music-theory"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <AnimatedExplainer
        src="/animations/music-theory/MT29-Pirates-Animated.mp4"
        caption="Chord trees in 'He's a Pirate' — D minor i · VI · III · VII"
        hint="≈ 22 sec · watch first"
      />
      <Chapter number="29" eyebrow="Music Theory · Chapter Twenty-Nine" title="Chord trees · Part 4: He's a Pirate">
        <p>
          Hey Jude was a perfect first test for the chord tree because it had lyrics —
          proper nouns and adjectives that pointed straight at the landing notes. Now we
          take the same tool to an instrumental: <em>He's a Pirate</em> from
          <em> Pirates of the Caribbean</em>. No words to lean on. Just a melody, a key,
          and the mathematics of three branches per tree.
        </p>
        <p>
          The piece is in <strong>D minor</strong>. That's the relative minor of F major
          (the <em>Hey Jude</em> key from last chapter), so the seven diatonic chords are
          the same — only the order and the tonal centre have shifted. We'll also flirt
          with <strong>D harmonic minor</strong>, because that's where the famous
          dominant chord (A major) sneaks in to give the piece its swashbuckling pull.
        </p>
        <MTQuote>
          In this case it makes it even more obvious because you don't have lyrics, so the
          composer repeats the D, or repeats the landing note whenever it is landing. That's
          clearly a land, right? It's on the 1, and then it repeats a little more, so it
          sets even more importance in our brain.
        </MTQuote>
        <NoteImage
          src="/notes/music-theory/finals/29a-pirates-part-1.png"
          caption="Jason's analysis of 'He's a Pirate' (Pirates of the Caribbean theme) — Part 1. Each phrase landing point has its chord(s) labelled (Dm, B♭, F, Gm, B♭/A, Dm, etc.). Hans Zimmer's hook is built from a tight chord cycle; once you see the harmony, the melody reveals itself as chord-tone lines decorated with passing notes."
        />
      </Chapter>

      <Chapter eyebrow="Reading the tune" title="Landing notes by repetition and length">
        <Prose>
          <p>
            <em>He's a Pirate</em> is in <strong>3/4</strong>: one-two-three, one-two-
            three. The principal motif lands the same pitch on beat one, then repeats and
            extends it. Without lyrics to pin importance, the composer uses
            <strong> repetition and duration</strong>: the note that is played longer or
            played more times wins.
          </p>
          <p>
            The first phrase circles around four landing notes:
          </p>
        </Prose>

        <Steps>
          <Step><strong>Phrase 1 lands:</strong> <span className="font-mono text-amber-300">D · F · E · D</span></Step>
          <Step><strong>Phrase 2 lands:</strong> <span className="font-mono text-amber-300">D · F · E · D</span> (same shape)</Step>
          <Step><strong>Phrase 3 introduces:</strong> <span className="font-mono text-amber-300">G</span> — the upper neighbour that pushes the harmony sideways</Step>
          <Step><strong>Final phrase resolves:</strong> back to <span className="font-mono text-amber-300">D</span></Step>
        </Steps>
      </Chapter>

      <Chapter eyebrow="The seven diatonic chords of D minor" title="Same notes as F major, different home">
        <TheoryBox label="The diatonic chords of D natural minor">
          <div className="font-mono text-[14px] text-amber-200">
            i = D min &nbsp;·&nbsp; ii° = E dim &nbsp;·&nbsp; ♭III = F maj &nbsp;·&nbsp; iv = G min &nbsp;·&nbsp; v = A min &nbsp;·&nbsp; ♭VI = B♭ maj &nbsp;·&nbsp; ♭VII = C maj
          </div>
          <div className="mt-2 text-[12px] text-nsm-dim">
            Notice the v is minor (A minor) in natural minor. In <em>harmonic</em>
            minor — with the raised 7th, C♯ — the v becomes a major <strong>V</strong>
            (A major), the dominant chord that drives the cadence in <em>He's a Pirate</em>.
          </div>
        </TheoryBox>
      </Chapter>

      <Chapter eyebrow="Walking the trees" title="Phrase 1: D · F · E · D">
        <Prose>
          <p>
            Same workflow as Hey Jude. List the diatonic candidates per landing note,
            narrow by context.
          </p>
        </Prose>

        <Steps>
          <Step>
            <strong>D — tree of D in D minor:</strong> <span className="font-mono">D minor</span> (root),
            <span className="font-mono"> B♭ major</span> (3rd), <span className="font-mono">G minor</span> (5th).
            We're starting the piece, so we want to <strong>establish the tonic</strong>.
            Pick <span className="font-mono text-amber-300">D minor</span>.
          </Step>
          <Step>
            <strong>F — tree of F in D minor:</strong> <span className="font-mono">F major</span> (root),
            <span className="font-mono"> D minor</span> (3rd), <span className="font-mono">B♭ major</span> (5th).
            Could stay on D minor (no harmonic motion) — but that's boring. Picking
            <span className="font-mono text-amber-300">B♭ major</span> gives a heroic,
            victorious shift, ♭VI under the melody.
          </Step>
          <Step>
            <strong>E — tree of E in D minor:</strong> <span className="font-mono">E diminished</span> (root),
            <span className="font-mono"> C major</span> (3rd), <span className="font-mono">A minor</span> (5th).
            Picking <span className="font-mono text-amber-300">A minor</span> sets up the
            return to D minor as a v → i motion.
          </Step>
          <Step>
            <strong>D — back home:</strong> <span className="font-mono text-amber-300">D minor</span>.
          </Step>
        </Steps>

        <TheoryBox label="Phrase 1 progression">
          <div className="font-mt text-base text-amber-200">
            D minor &nbsp;→&nbsp; B♭ major &nbsp;→&nbsp; A minor &nbsp;→&nbsp; D minor
          </div>
          <div className="mt-2 text-[12px] text-nsm-dim">
            i &nbsp;→&nbsp; ♭VI &nbsp;→&nbsp; v &nbsp;→&nbsp; i — pure D natural minor.
          </div>
        </TheoryBox>
      </Chapter>

      <Chapter eyebrow="Phrase 2 — same melody, different harmony" title="The reharmonisation moment">
        <Prose>
          <p>
            The melody repeats: D, F, E, D again. If we play the same chords, the music
            stops moving. So the composer makes the melody <em>do less work</em> by
            letting the harmony do more. We pick different branches from the same trees.
          </p>
        </Prose>

        <Steps>
          <Step><strong>D:</strong> this time pick <span className="font-mono text-amber-300">B♭ major</span> — D as the major 3rd of B♭. A different colour for the same melody note.</Step>
          <Step><strong>F:</strong> pick <span className="font-mono text-amber-300">F major</span> — the ♭III of D minor.</Step>
          <Step><strong>E:</strong> pick <span className="font-mono text-amber-300">C major</span> — E as the 3rd of C, the ♭VII of D minor.</Step>
          <Step><strong>D:</strong> end on <span className="font-mono text-amber-300">D minor</span> — the home keeps its hold.</Step>
        </Steps>

        <TheoryBox label="Phrase 2 progression">
          <div className="font-mt text-base text-amber-200">
            B♭ major &nbsp;→&nbsp; F major &nbsp;→&nbsp; C major &nbsp;→&nbsp; D minor
          </div>
          <div className="mt-2 text-[12px] text-nsm-dim">
            ♭VI &nbsp;→&nbsp; ♭III &nbsp;→&nbsp; ♭VII &nbsp;→&nbsp; i — the
            three-flat-major sweep that gives the piece its anthemic lift.
          </div>
        </TheoryBox>
      </Chapter>

      <Chapter eyebrow="Phrase 3" title="The G arrives — and the iv chord answers">
        <Prose>
          <p>
            The melody finally moves out of D-F-E orbit and lands on a <strong>G</strong>.
            The tree of G in D minor: <span className="font-mono">G minor</span> (root),
            <span className="font-mono"> E diminished</span> (3rd),
            <span className="font-mono"> C major</span> (5th).
          </p>
          <p>
            The composer sticks with <strong>G minor</strong> — the iv of D minor — and
            often slips a <strong>B♭ in the bass</strong> under it (Gm/B♭), which is a
            slash chord we'll meet properly in the next chapter on chord charts. That
            B♭ bass keeps the harmonic floor moving even when the chord overhead doesn't
            change.
          </p>
        </Prose>
      </Chapter>

      <Chapter eyebrow="The cadence" title="Where harmonic minor enters">
        <Prose>
          <p>
            For the final phrase, the composer reaches for the <strong>dominant V</strong>
            — and that requires raising the 7th of the scale (C → C♯), which is what
            <em> harmonic minor</em> does. The chord on A is now <strong>A major</strong>
            (A&nbsp;C♯&nbsp;E), full of pull toward home.
          </p>
        </Prose>

        <TheoryBox label="The Pirate cadence">
          <div className="font-mt text-base text-amber-200">
            G minor &nbsp;→&nbsp; A major (V) &nbsp;→&nbsp; D minor (i)
          </div>
          <div className="mt-2 text-[12px] text-nsm-dim">
            iv → V → i — classic minor-key cadence. The dominant A major is borrowed
            from D harmonic minor; without it, the piece would sound modal and
            unresolved instead of epic and definitive.
          </div>
        </TheoryBox>

        <Prose>
          <p>
            The whole song follows the same rules. Every landing note picks one of three
            chords from its tree. The composer alternates between obvious choices and
            adventurous ones, and that is the entire harmonic story of the piece.
          </p>
        </Prose>
      </Chapter>

      <Chapter eyebrow="The tool" title="Try the tree of D minor">
        <ChordTree tonic="D" mode="minor" />

        <Prose>
          <p>
            Click each chord and sing the corresponding D, F, E or G of the motif. Listen
            for which branches make the melody soar and which keep it grounded.
          </p>
        </Prose>
      </Chapter>

      <Chapter eyebrow="The bigger lesson" title="Theory as preparation, ear as instinct">
        <MTQuote>
          Ear training is generally about instinct, while music theory is more about
          preparation, so to speak. So, if I were to give you a jungle example, the instinct
          is that you know that you are under attack, but the preparation is: what do you do
          when you are under attack?
        </MTQuote>

        <Prose>
          <p>
            That's the close of the four-chapter chord-tree series. You now have a
            constraint system that turns "what chord goes here?" from a guess into a
            short list — in any major or minor key, with or without lyrics, with or
            without the audio in your ears. It works on songs you've never heard, on
            songs you wrote last night, and on every melody you'll ever harmonise from
            this point forward.
          </p>
        </Prose>
        <NoteImage
          src="/notes/music-theory/finals/29b-pirates-part-2.png"
          caption="Pirates Part 2 — the harmonic continuation, including the inversions Dm/A and Gm7. The piece never leaves D minor for long, but Zimmer's voice-leading creates the illusion of motion by inverting and adding extensions inside the same chord family."
        />
      </Chapter>

      <NextUp
        to="/lesson/music-theory-30"
        label="Chapter 30 · How to Read Chord Charts"
        why="Now that we know which chords to choose, we need to write them down so other musicians can read them. Chord charts are the universal shorthand — capital letters, sevenths, slash notation and all."
      />
    </LessonShell>
  );
}
